import { FC, useEffect } from 'react';
import {
  Panel,
  TextField,
  SwitchInput,
  CheckboxGroupInput,
  DateTimeInput,
  DataLossPrompt,
  Footer,
  Button,
  NumberInput,
} from 'melp-design/components';
import { Controller, SubmitHandler, useForm } from 'react-hook-form-latest';
import { Collapse, Divider, Stack } from '@mui/material';
import moment from 'moment';
import { useTranslation } from 'react-i18next';
import {
  getSurveyPermissionName,
  NotificationMethod,
  notificationMethods,
  useActivateSurvey,
  useSurvey,
  useUpdateSurvey,
} from 'store/surveys';
import { attemptsToRetries, retriesToAttempts } from 'containers/surveys';
import { ROUTES } from 'config';
import { usePredefinedToasts } from 'utils/Toast';
import { useRouteParams } from 'utils/useIdParam';
import { usePointsLabel } from 'store/recognitions';
import { useMe } from 'state/Administrators';

interface Values {
  name: string;
  anonymous: boolean;
  hasWebAccess: boolean;
  notificationMethods: NotificationMethod[];
  scoringEnabled: boolean;
  minScore: number;
  attempts: number;
  rewardsEnabled: boolean;
  rewardPoints: number;
  publishDateTime: string | null;
  endDateTime: string | null;
}

const INITIAL_VALUES: Values = {
  name: '',
  anonymous: false,
  hasWebAccess: false,
  notificationMethods: [],
  publishDateTime: null,
  endDateTime: null,
  scoringEnabled: false,
  minScore: 0,
  attempts: 1,
  rewardsEnabled: false,
  rewardPoints: 0,
};

export const SurveySettings: FC = () => {
  const { t } = useTranslation();
  const { id: surveyId } = useRouteParams(ROUTES.surveys.survey.details);
  const predefinedToasts = usePredefinedToasts();
  const pointsLabel = usePointsLabel();
  const { isFeatureEnabled, hasEditPermission } = useMe();

  const {
    control,
    handleSubmit,
    reset,
    watch,
    formState: { isDirty, errors },
  } = useForm<Values>({ defaultValues: INITIAL_VALUES });

  const { data: survey } = useSurvey(surveyId);
  const hasSurveyEditPermission = survey
    ? hasEditPermission([getSurveyPermissionName(survey.type)])
    : false;
  const canEdit = hasSurveyEditPermission;

  const { mutate: updateSurvey, isLoading: isUpdating } = useUpdateSurvey();
  const { mutate: activateSurvey, isLoading: isActivating } =
    useActivateSurvey();

  const scoringUnavailable =
    survey?.type === 'training' &&
    !survey.questions.some((question) => question.type !== 'information_page');

  useEffect(() => {
    if (survey) {
      const hasQuestionPage = survey.questions.some(
        (question) => question.type !== 'information_page',
      );

      reset({
        name: survey.name,
        anonymous: survey.anonymous,
        hasWebAccess: survey.hasWebAccess,
        notificationMethods: survey.notificationMethods,
        scoringEnabled:
          survey.type === 'training' && !hasQuestionPage
            ? false
            : survey.scoringEnabled,
        minScore: survey.minScore,
        attempts: retriesToAttempts(survey.retries),
        rewardsEnabled: survey.rewardPoints > 0,
        rewardPoints: survey.rewardPoints,
        publishDateTime: survey.publishDateTime,
        endDateTime: survey.endDateTime,
      });
    }
  }, [reset, survey]);

  const onSubmit: SubmitHandler<Values> = ({
    scoringEnabled,
    minScore,
    attempts,
    rewardsEnabled,
    rewardPoints,
    ...values
  }) => {
    updateSurvey(
      {
        surveyId,
        data: {
          ...(survey?.type !== 'survey'
            ? {
                scoringEnabled: scoringUnavailable ? false : scoringEnabled,
                minScore,
                retries: attemptsToRetries(attempts),
              }
            : {}),
          rewardPoints: rewardsEnabled ? rewardPoints : 0,
          ...values,
        },
      },
      {
        onSuccess: () => {
          predefinedToasts.success.updated();
        },
      },
    );
  };

  const onPublish: SubmitHandler<Values> = ({
    scoringEnabled,
    minScore,
    attempts,
    rewardsEnabled,
    rewardPoints,
    ...values
  }) => {
    updateSurvey(
      {
        surveyId,
        data: {
          ...values,
          ...(survey?.type !== 'survey'
            ? {
                scoringEnabled: scoringUnavailable ? false : scoringEnabled,
                minScore,
                retries: attemptsToRetries(attempts),
              }
            : {}),
          rewardPoints: rewardsEnabled ? rewardPoints : 0,
          publishDateTime: values.publishDateTime || moment().toISOString(true),
        },
      },
      {
        onSuccess: () => {
          activateSurvey(
            { surveyId },
            {
              onSuccess: () => {
                predefinedToasts.success.updated();
              },
            },
          );
        },
      },
    );
  };

  const [publishDateTime, scoringEnabled, rewardsEnabled] = watch([
    'publishDateTime',
    'scoringEnabled',
    'rewardsEnabled',
  ]);

  return (
    <>
      <form onSubmit={handleSubmit(onSubmit)}>
        <Stack gap={2}>
          <Panel title={t('common.settings')}>
            <Stack spacing={2}>
              <Controller
                name="name"
                control={control}
                render={({ field: { name, value, onChange, ref } }) => (
                  <TextField
                    label={t('surveys.systemName')}
                    name={name}
                    value={value}
                    onChange={onChange}
                    required
                    disabled={!canEdit}
                    ref={ref}
                  />
                )}
              />

              {survey?.type !== 'training' ? (
                <Controller
                  name="anonymous"
                  control={control}
                  render={({ field: { name, value, onChange, ref } }) => {
                    return (
                      <SwitchInput
                        label={t('surveys.isAnonymousInputTitle')}
                        name={name}
                        value={value}
                        onChange={onChange}
                        disabled={!canEdit}
                        ref={ref}
                      />
                    );
                  }}
                />
              ) : null}

              <Controller
                name="notificationMethods"
                control={control}
                render={({ field: { name, value, onChange, ref } }) => {
                  return (
                    <CheckboxGroupInput
                      label={t('surveys.setNotifications')}
                      items={notificationMethods.map((method) => ({
                        label: t(`news.send_method.${method}`),
                        value: method,
                      }))}
                      name={name}
                      value={value}
                      onChange={onChange}
                      disabled={!canEdit}
                      ref={ref}
                    />
                  );
                }}
              />
            </Stack>
          </Panel>

          {survey?.type === 'training' ? (
            <Panel title={t('surveys.scoring.title')}>
              <Stack gap={2}>
                <Controller
                  name="scoringEnabled"
                  control={control}
                  render={({ field: { name, value, onChange, ref } }) => {
                    return (
                      <SwitchInput
                        label={t('surveys.scoringEnabled.label')}
                        name={name}
                        value={scoringUnavailable ? false : value}
                        onChange={onChange}
                        disabled={!canEdit || scoringUnavailable}
                        info={
                          scoringUnavailable
                            ? t('surveys.scoringEnabled.unavailable')
                            : undefined
                        }
                        ref={ref}
                      />
                    );
                  }}
                />

                <Collapse in={scoringEnabled && !scoringUnavailable}>
                  <Stack flexDirection="row" gap={2}>
                    <Controller
                      name="minScore"
                      control={control}
                      rules={{
                        min: {
                          value: 0,
                          message: t('common.minValueValidationError', {
                            min: 0,
                          }),
                        },
                        max: {
                          value: 100,
                          message: t('common.maxValueValidationError', {
                            max: 100,
                          }),
                        },
                      }}
                      render={({ field: { name, value, onChange, ref } }) => (
                        <NumberInput
                          label={t('surveys.minScore.label')}
                          name={name}
                          value={value}
                          onChange={onChange}
                          errorMessage={errors.minScore?.message}
                          key={value}
                          disabled={!canEdit}
                          ref={ref}
                          InputProps={{ endAdornment: '%' }}
                          sx={{ width: '100%' }}
                          type="integer"
                        />
                      )}
                    />

                    <Controller
                      name="attempts"
                      control={control}
                      rules={{
                        min: {
                          value: 1,
                          message: t('common.minValueValidationError', {
                            min: 1,
                          }),
                        },
                        validate: (value) =>
                          Number.isInteger(value) ||
                          t('common.patternValidationError'),
                      }}
                      render={({ field: { name, value, onChange, ref } }) => (
                        <NumberInput
                          label={t('surveys.attempts.label')}
                          name={name}
                          value={value}
                          onChange={onChange}
                          errorMessage={errors.attempts?.message}
                          key={value}
                          disabled={!canEdit}
                          ref={ref}
                          sx={{ width: '100%' }}
                          type="integer"
                        />
                      )}
                    />
                  </Stack>
                </Collapse>
              </Stack>
            </Panel>
          ) : null}

          {isFeatureEnabled('recognition') ? (
            <Panel title={t('recognition.rewardsTab')}>
              <Stack gap={2}>
                <Controller
                  name="rewardsEnabled"
                  control={control}
                  render={({ field: { name, value, onChange, ref } }) => {
                    return (
                      <SwitchInput
                        label={t('surveys.rewardsEnabled.label', {
                          pointName: pointsLabel,
                        })}
                        disabled={
                          !canEdit ||
                          !hasEditPermission(['employeeRecognition'])
                        }
                        name={name}
                        value={value}
                        onChange={onChange}
                        ref={ref}
                      />
                    );
                  }}
                />

                <Collapse in={rewardsEnabled}>
                  <Stack flexDirection="row" gap={2}>
                    <Controller
                      name="rewardPoints"
                      control={control}
                      rules={{
                        min: {
                          value: 0,
                          message: t('common.minValueValidationError', {
                            min: 0,
                          }),
                        },
                      }}
                      render={({ field: { name, value, onChange, ref } }) => (
                        <NumberInput
                          disabled={
                            !canEdit ||
                            !hasEditPermission(['employeeRecognition'])
                          }
                          name={name}
                          value={value}
                          onChange={onChange}
                          errorMessage={errors.rewardPoints?.message}
                          key={value}
                          ref={ref}
                          InputProps={{ endAdornment: pointsLabel }}
                          sx={{ width: '25%' }}
                          type="integer"
                        />
                      )}
                    />
                  </Stack>
                </Collapse>
              </Stack>
            </Panel>
          ) : null}

          <Panel title={t('news.publishing.label')}>
            <Stack gap={2}>
              <Stack flexDirection="row" gap={2}>
                <Controller
                  name="publishDateTime"
                  control={control}
                  render={({ field: { name, value, onChange, ref } }) => (
                    <DateTimeInput
                      label={t('surveys.startDate')}
                      name={name}
                      value={value}
                      onChange={onChange}
                      required={survey?.status === 'pending'}
                      datePickerProps={{
                        showTimeOffset: true,
                      }}
                      disabled={!canEdit}
                      ref={ref}
                    />
                  )}
                />

                <Divider orientation="vertical" variant="middle" flexItem />

                <Controller
                  name="endDateTime"
                  control={control}
                  render={({ field: { name, value, onChange, ref } }) => (
                    <DateTimeInput
                      label={t('surveys.endDate')}
                      name={name}
                      value={value}
                      onChange={onChange}
                      datePickerProps={{
                        showTimeOffset: true,
                      }}
                      disabled={!canEdit}
                      ref={ref}
                    />
                  )}
                />
              </Stack>

              {survey?.type === 'survey' ? (
                <Controller
                  name="hasWebAccess"
                  control={control}
                  render={({ field: { name, value, onChange, ref } }) => {
                    return (
                      <SwitchInput
                        label={t('surveys.hasWebAccess.label')}
                        name={name}
                        value={value}
                        onChange={onChange}
                        disabled={survey?.status !== 'draft' || !canEdit}
                        ref={ref}
                      />
                    );
                  }}
                />
              ) : null}
            </Stack>
          </Panel>
        </Stack>

        <Footer visible={isDirty || survey?.status === 'draft'}>
          <Stack direction="row" justifyContent="end" gap="10px">
            {isDirty ? (
              <>
                <Button
                  label={t('common.cancel')}
                  variant="neutral-outline"
                  onClick={() => {
                    reset(undefined, { keepDirty: false });
                  }}
                />
                <Button
                  label={t('common.save')}
                  variant="secondary"
                  type="submit"
                  disabled={isUpdating || !hasSurveyEditPermission}
                />
              </>
            ) : null}

            {survey?.status === 'draft' ? (
              <Button
                label={
                  publishDateTime
                    ? t('news.publish', {
                        publishDate: moment(publishDateTime).format('l'),
                        interpolation: { escapeValue: false },
                      })
                    : t('actions.publish_now')
                }
                variant="primary"
                disabled={
                  isUpdating || isActivating || !hasSurveyEditPermission
                }
                onClick={handleSubmit(onPublish)}
              />
            ) : null}
          </Stack>
        </Footer>
      </form>

      <DataLossPrompt when={!hasSurveyEditPermission ? false : isDirty} />
    </>
  );
};
